import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Divider, 
  Button,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  TextField,
  InputAdornment
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RoadIcon from '@mui/icons-material/Timeline';
import SensorsIcon from '@mui/icons-material/Sensors';
import LayersIcon from '@mui/icons-material/Layers';
import SearchIcon from '@mui/icons-material/Search';
// Import user context to get role and organization
import { useAuth } from '../../../context/AuthContext';

// Placeholder data structures - these will be passed as props
// const organizationsList = [{ id: 1, name: 'Org A' }];
// const networksList = [{ id: 1, name: 'Network 1' }];
// const sectionsList = [{ id: 1, name: 'Section X' }];
// const categoriesList = [{ id: 1, name: 'Category 1' }];
// const subtypesList = [{ id: 2, name: 'Subtype A' }];

const FilterPanel = ({
  // Filter options data (arrays of objects with id, name)
  organizations = [],
  networks = [],
  sections = [],
  categories = [],
  types = [],
  subtypes = [],
  regions = [], // Dynamic regions from sections, not hardcoded
  // Currently selected filter values (IDs or '')
  selectedOrganizationId = '',
  selectedNetworkId = '',
  selectedSectionId = '',
  selectedRegion = '',
  selectedCategoryId = '',
  selectedTypeId = '',
  selectedSubtypeId = '',
  selectedStatus = '',
  // Callback functions to update state in parent
  onOrganizationChange,
  onNetworkChange,
  onSectionChange,
  onRegionChange,
  onCategoryChange,
  onTypeChange,
  onSubtypeChange,
  onStatusChange,
  onResetFilters, // Added prop for reset handler
  // Counts for display
  displayedCount = 0,
  totalCount = null, // Can be null initially
  // Layer visibility controls
  showRoadLines = true,
  setShowRoadLines,
  showIoTSensors = true,
  setShowIoTSensors,
  // Removed 3D model visibility control
  // Removed showAssetFootprints props
  // Removed asset points visibility props
  // Search term props
  searchTerm = '',
  onSearchTermChange,
  assets = [] // Add assets prop with a default value
}) => {
  const theme = useTheme();
  // Get user context to make organization selection role-aware
  const { user, isAuthenticated, isLoading } = useAuth();
  
  // Fix user data access - remove double nesting
  const isSystemAdmin = user?.role === 'admin' && user?.organizationId === null;
  const userOrganizationId = user?.organizationId;
  
  // Auto-select organization for non-admin users
  React.useEffect(() => {
    if (user && !isSystemAdmin && userOrganizationId && !selectedOrganizationId) {
      // Auto-select user's organization for non-admin users
      if (onOrganizationChange) {
        onOrganizationChange(userOrganizationId);
      }
    }
  }, [user, isSystemAdmin, userOrganizationId, selectedOrganizationId, onOrganizationChange]);

  const handleFilterChange = (event, handler) => {
    if (handler) {
      handler(event.target.value); // Pass the selected ID (or '')
    }
  };

  // Show loading state if authentication is still loading
  if (isLoading) {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'absolute',
          top: 70,
          left: 16,
          zIndex: 10,
          padding: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          borderRadius: 2.5,
          minWidth: '340px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100px'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading authentication...
        </Typography>
      </Paper>
    );
  }

  // Asset status options
  const statusOptions = [
    { value: 'under_construction', label: 'Under Construction' },
    { value: 'under_renovation', label: 'Under Renovation' },
    { value: 'active', label: 'Active' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'decommissioned', label: 'Decommissioned' }
  ];

  // Determine the effective organization ID to display
  const effectiveOrganizationId = isSystemAdmin ? selectedOrganizationId : userOrganizationId;
  
  // Find the user's organization name for display
  const userOrganization = organizations.find(org => org.id === userOrganizationId);
  const userOrganizationName = userOrganization?.name || 'Your Organization';

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        top: 70, // Position below the filter button
        left: 16, // Align with the filter button
        zIndex: 10,
        padding: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.95), // Slightly more opaque
        backdropFilter: 'blur(10px)',
        borderRadius: 2.5, // Slightly more rounded corners
        minWidth: '340px', // Slightly wider
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.2, // Slightly more spacing between elements
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)',
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(-20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterListIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem', color: theme.palette.primary.main }}>
            Filter Assets
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={showRoadLines ? "Hide Highway Network" : "Show Highway Network"}>
            <IconButton 
              size="small" 
              onClick={() => setShowRoadLines(!showRoadLines)}
              sx={{ 
                color: showRoadLines ? 'success.main' : alpha(theme.palette.text.secondary, 0.5),
                bgcolor: showRoadLines ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                border: showRoadLines ? `1px solid ${alpha(theme.palette.success.main, 0.2)}` : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                '&:hover': {
                  bgcolor: showRoadLines 
                    ? alpha(theme.palette.success.main, 0.15) 
                    : alpha(theme.palette.action.hover, 0.1)
                }
              }}
            >
              <RoadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={showIoTSensors ? "Hide IoT Sensors" : "Show IoT Sensors"}>
            <IconButton 
              size="small" 
              onClick={() => setShowIoTSensors(!showIoTSensors)}
              sx={{ 
                color: showIoTSensors ? 'error.main' : alpha(theme.palette.text.secondary, 0.5),
                bgcolor: showIoTSensors ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                border: showIoTSensors ? `1px solid ${alpha(theme.palette.error.main, 0.2)}` : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                '&:hover': {
                  bgcolor: showIoTSensors 
                    ? alpha(theme.palette.error.main, 0.15) 
                    : alpha(theme.palette.action.hover, 0.1)
                }
              }}
            >
              <SensorsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Removed 3D Model button */}
          {/* Remove Asset Footprints button */}
          {/* Removed Asset Points Toggle Button */}
        </Box>
      </Box>

      {/* Search Input - Enhanced with icon and styling */}
      <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
        <TextField
          placeholder="Search Assets"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.9rem',
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
              },
              '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                boxShadow: '0 3px 8px rgba(0,0,0,0.08)'
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
          }}
        />
      </FormControl>

      {/* Location Filter Group */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600, 
            color: alpha(theme.palette.text.primary, 0.7),
            mb: 0.5,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          }}
        >
          Location Filters
        </Typography>

        {/* Organization Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
          <InputLabel id="org-filter-label" sx={{ fontSize: '0.85rem' }}>
            {isSystemAdmin ? 'Organization' : 'Organization (Auto-selected)'}
          </InputLabel>
          <Select
            labelId="org-filter-label"
            label={isSystemAdmin ? 'Organization' : 'Organization (Auto-selected)'}
            value={effectiveOrganizationId || ''}
            onChange={(e) => handleFilterChange(e, onOrganizationChange)}
            disabled={!isSystemAdmin} // Disable for non-admin users
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem',
              // Style for disabled state to show it's auto-selected
              '&.Mui-disabled': {
                backgroundColor: alpha(theme.palette.success.main, 0.05),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.success.main, 0.3),
                }
              }
            }}
          >
            {isSystemAdmin && (
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Organizations</em>
              </MenuItem>
            )}
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id} sx={{ fontSize: '0.85rem' }}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
          {!isSystemAdmin && (
            <Typography 
              variant="caption" 
              color="success.main" 
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontWeight: 500 }}
            >
              ✓ Auto-selected: {userOrganizationName}
            </Typography>
          )}
        </FormControl>

        {/* Network and Region Filters - Side by Side */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Network Filter */}
          <FormControl size="small" variant="outlined" sx={{ mb: 0.5, flex: 1 }}>
            <InputLabel 
              id="network-filter-label" 
              sx={{ fontSize: '0.85rem' }}
              disabled={!effectiveOrganizationId}
            >
              Network
            </InputLabel>
            <Select
              labelId="network-filter-label"
              label="Network"
              value={selectedNetworkId}
              onChange={(e) => handleFilterChange(e, onNetworkChange)}
              disabled={!effectiveOrganizationId}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Networks</em>
              </MenuItem>
              {networks.map((net) => {
                return (
                  <MenuItem 
                    key={net.id} 
                    value={net.id} 
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {net.name}
                  </MenuItem>
                );
              })}
            </Select>
            {!effectiveOrganizationId && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
              >
                Select an organization first
              </Typography>
            )}
          </FormControl>

          {/* Region Filter */}
          <FormControl size="small" variant="outlined" sx={{ mb: 0.5, flex: 1 }}>
            <InputLabel 
              id="region-filter-label" 
              sx={{ fontSize: '0.85rem' }}
              disabled={!effectiveOrganizationId}
            >
              Region
            </InputLabel>
            <Select
              labelId="region-filter-label"
              label="Region"
              value={selectedRegion}
              onChange={(e) => handleFilterChange(e, onRegionChange)}
              disabled={!effectiveOrganizationId}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Regions</em>
              </MenuItem>
              {regions.map((region) => {
                return (
                  <MenuItem 
                    key={region} 
                    value={region} 
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {region}
                  </MenuItem>
                );
              })}
            </Select>
            {!effectiveOrganizationId && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
              >
                Select an organization first
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Section Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
          <InputLabel id="section-filter-label" sx={{ fontSize: '0.85rem' }}>Section</InputLabel>
          <Select
            labelId="section-filter-label"
            label="Section"
            value={selectedSectionId}
            onChange={(e) => handleFilterChange(e, onSectionChange)}
            disabled={!effectiveOrganizationId}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
              <em>All Sections</em>
            </MenuItem>
            {sections.map((sec) => {
              return (
                <MenuItem 
                  key={sec.id} 
                  value={sec.id} 
                  sx={{ fontSize: '0.85rem' }}
                >
                  {sec.name}
                </MenuItem>
              );
            })}
          </Select>
          {!effectiveOrganizationId && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
            >
              Select an organization first
            </Typography>
          )}
        </FormControl>
      </Box>

      {/* Asset Type Filter Group */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
        mt: 0.5
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600, 
              color: alpha(theme.palette.text.primary, 0.7),
              fontSize: '0.75rem',
              letterSpacing: '0.03em',
              textTransform: 'uppercase'
            }}
          >
            Asset Type Filters
          </Typography>
          
          {/* Status filter - subtle dropdown */}
          <FormControl size="small" sx={{ minWidth: '120px' }}>
            <Select
              value={selectedStatus}
              onChange={(e) => handleFilterChange(e, onStatusChange)}
              displayEmpty
              variant="standard"
              sx={{
                fontSize: '0.75rem',
                '&:before, &:after': { display: 'none' },
                '& .MuiSelect-select': { 
                  paddingY: 0.3,
                  paddingX: 0.5,
                  color: selectedStatus ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.7),
                  fontWeight: selectedStatus ? 500 : 400,
                  bgcolor: selectedStatus ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12)
                  }
                }
              }}
              IconComponent={(props) => (
                <Box component="svg" 
                  viewBox="0 0 24 24" 
                  sx={{ 
                    width: 16, 
                    height: 16, 
                    color: alpha(theme.palette.text.primary, 0.5), 
                    ml: -0.5, 
                    ...props.sx 
                  }}
                >
                  <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                </Box>
              )}
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                <em>Status: Any</em>
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Category Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
          <InputLabel id="category-filter-label" sx={{ fontSize: '0.85rem' }}>Category</InputLabel>
          <Select
            labelId="category-filter-label"
            label="Category"
            value={selectedCategoryId}
            onChange={(e) => handleFilterChange(e, onCategoryChange)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Categories</em></MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.85rem' }}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Type Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
          <InputLabel id="type-filter-label" sx={{ fontSize: '0.85rem' }}>Type</InputLabel>
          <Select
            labelId="type-filter-label"
            label="Type"
            value={selectedTypeId}
            onChange={(e) => handleFilterChange(e, onTypeChange)}
            disabled={!selectedCategoryId}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Types</em></MenuItem>
            {types.map((type) => (
              <MenuItem key={type.id} value={type.id} sx={{ fontSize: '0.85rem' }}>{type.name}</MenuItem>
            ))}
          </Select>
          {!selectedCategoryId && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
            >
              Select a category first
            </Typography>
          )}
        </FormControl>

        {/* Subtype Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
          <InputLabel id="subtype-filter-label" sx={{ fontSize: '0.85rem' }}>Subtype</InputLabel>
          <Select
            labelId="subtype-filter-label"
            label="Subtype"
            value={selectedSubtypeId}
            onChange={(e) => handleFilterChange(e, onSubtypeChange)}
            disabled={!selectedTypeId}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Subtypes</em></MenuItem>
            {subtypes.map((sub) => (
              <MenuItem key={sub.id} value={sub.id} sx={{ fontSize: '0.85rem' }}>{sub.name}</MenuItem>
            ))}
          </Select>
          {!selectedTypeId && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
            >
              Select a type first
            </Typography>
          )}
        </FormControl>
      </Box>

      {/* Reset Button */}
      <Button
        variant="outlined"
        startIcon={<RestartAltIcon sx={{ fontSize: '1rem' }} />}
        onClick={onResetFilters}
        sx={{ 
          mt: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.85rem',
          py: 0.7,
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.5),
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderColor: theme.palette.primary.main,
            boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)'
          }
        }}
      >
        Reset Filters
      </Button>

      {/* Display Count Status */}
      <Box 
        sx={{ 
          mt: 1.5,
          py: 0.9, 
          px: 1.5, 
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.03)'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 500,
            fontSize: '0.8rem'
          }}
        >
          {totalCount !== null
            ? `Displaying ${displayedCount} of ${totalCount} assets`
            : `Displaying ${displayedCount} assets`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default FilterPanel;